import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, Plus, Eye, Pin, Ban } from "lucide-react";
import StageShell from "@/components/StageShell";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ManualJD from "@/components/jd/ManualJD";
import { useRun } from "@/hooks/useRun";
import { api, errMsg, LEVELS } from "@/lib/api";

const ST = {
  usable: "text-emerald-300 border-emerald-500/40", duplicate: "text-slate-400 border-slate-600", off_level: "text-amber-300 border-amber-500/40",
  unusable: "text-rose-300 border-rose-500/40", extra: "text-slate-400 border-slate-700", excluded: "text-rose-400 border-rose-600/50", new: "text-blue-300 border-blue-500/40",
};
const inp = "w-full bg-slate-900 border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-blue-500";

function ScraperForm({ job, form, setForm }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  return (
    <div className="card-surface grid grid-cols-6 gap-3 items-end" data-testid="scraper-form">
      <label className="col-span-2"><span className="label-xs block mb-1">Target title</span><input data-testid="scraper-title-input" className={inp} value={form.title} onChange={set("title")} /></label>
      <label><span className="label-xs block mb-1">Level</span>
        <select data-testid="scraper-level-select" className={inp} value={form.level} onChange={set("level")}>{LEVELS.map((l) => <option key={l}>{l}</option>)}</select></label>
      <label className="col-span-2"><span className="label-xs block mb-1">Locations (separate with ;)</span><input data-testid="scraper-locations-input" className={inp} value={form.locations} onChange={set("locations")} /></label>
      <label><span className="label-xs block mb-1">Posted within (days)</span>
        <select data-testid="scraper-days-select" className={inp} value={form.days} onChange={set("days")}>{[7, 14, 30, 60, 90].map((d) => <option key={d}>{d}</option>)}</select></label>
      <label className="flex items-center gap-2 text-sm col-span-6">
        <input data-testid="scraper-remote-checkbox" type="checkbox" checked={form.remote} onChange={set("remote")} className="accent-blue-500" /> Include remote (US)
        <span className="text-xs text-muted-foreground ml-4">Also searches the client's target companies ({(job.client.target_companies || []).length}) and industries ({(job.client.industries || []).length}).</span>
      </label>
    </div>
  );
}

export default function Stage1({ job, refresh }) {
  const c = job.client;
  const [jds, setJds] = useState([]);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState(null);
  const [manual, setManual] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [form, setForm] = useState({ title: job.track_title, level: c.seniority || "Mid", locations: (c.target_locations || []).join("; "), remote: true, days: "30" });
  const load = useCallback(() => api.get(`/jobs/${job.id}/jds`).then(({ data }) => setJds(data)), [job.id]);
  useEffect(() => { load(); }, [load]);
  const runHook = useRun(job.id, 1, () => { load(); refresh(); });

  const patch = async (d, upd) => { await api.patch(`/jds/${d.id}`, upd); load(); };
  const importFile = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post(`/jobs/${job.id}/jds/import`, fd); toast.success(`${data.added} JDs imported. Click "Re-check JDs" to process.`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const counts = jds.reduce((a, d) => ({ ...a, [d.status]: (a[d.status] || 0) + 1 }), {});
  const shown = filter === "all" ? jds : jds.filter((d) => d.status === filter);
  const params = () => ({ ...form, days: Number(form.days), locations: form.locations.split(";").map((x) => x.trim()).filter(Boolean) });

  return (
    <StageShell job={job} stage={1} refresh={refresh} runHook={runHook}
      runs={[{ action: "jd_scrape", label: "Collect JDs", params, testid: "jd-scrape-button" }, { action: "jd_process", label: "Re-check JDs", testid: "jd-process-button" }]}>
      <button data-testid="toggle-search-settings" onClick={() => setShowSearch(!showSearch)} className="text-xs text-slate-400 hover:text-slate-200">
        {showSearch ? "Hide" : "Change"} search settings (title, level, locations, date range)
      </button>
      {jds.length > 0 && (counts.usable || 0) < 50 && (
        <div data-testid="jd-count-note" className={`text-sm rounded-lg border px-3 py-2 ${(counts.usable || 0) >= 20 ? "border-amber-500/40 text-amber-200" : "border-rose-500/40 text-rose-200"}`}>
          {(counts.usable || 0) >= 20
            ? `${counts.usable} usable JDs. The target is 50, but 20 or more is enough to continue, which is fine for niche roles.`
            : `${counts.usable || 0} usable JDs. You need at least 20 to continue (target 50). Try a related title, more locations or a longer date range, or paste JDs.`}
        </div>
      )}
      {(showSearch || !jds.length || (counts.usable || 0) < 20) && <ScraperForm job={job} form={form} setForm={setForm} />}
      <div className="flex items-center gap-2 flex-wrap">
        {["all", "usable", "new", "duplicate", "off_level", "unusable", "extra", "excluded"].map((s) => (
          <button key={s} data-testid={`jd-filter-${s}`} onClick={() => setFilter(s)}
            className={`px-3 py-1 rounded-full text-xs border ${filter === s ? "bg-blue-600/20 border-blue-500/50 text-blue-200" : "border-border text-slate-400"}`}>
            {s.replace("_", " ")} {s === "all" ? jds.length : counts[s] || 0}
          </button>
        ))}
        <div className="ml-auto flex gap-2">
          <Button data-testid="jd-manual-add-button" variant="secondary" size="sm" onClick={() => setManual(true)}><Plus size={13} className="mr-1" />Paste JD</Button>
          <label className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-md bg-secondary hover:bg-slate-700 cursor-pointer">
            <Upload size={13} /> Import JSON/CSV
            <input data-testid="jd-import-input" type="file" accept=".json,.csv" className="hidden" onChange={(e) => { e.target.files[0] && importFile(e.target.files[0]); e.target.value = ""; }} />
          </label>
        </div>
      </div>
      <div className="rounded-xl border border-border overflow-hidden">
        <table data-testid="jd-collection-table" className="w-full text-sm">
          <thead className="bg-slate-900/70 text-left text-[11px] uppercase tracking-wider text-muted-foreground">
            <tr><th className="px-3 py-2.5">Company</th><th className="px-3">Title</th><th className="px-3">Level</th><th className="px-3">Location</th><th className="px-3">Posted</th><th className="px-3">Source</th><th className="px-3">Status</th><th /></tr>
          </thead>
          <tbody>
            {shown.map((d) => (
              <tr key={d.id} className="border-t border-border hover:bg-slate-900/40" data-testid={`jd-row-${d.id}`}>
                <td className="px-3 py-2 font-medium">{d.company}</td>
                <td className="px-3">{d.url ? <a href={d.url} target="_blank" rel="noreferrer" className="hover:text-blue-300">{d.title}</a> : d.title}</td>
                <td className="px-3">
                  <select value={d.level_override || d.level_ai || ""} onChange={(e) => patch(d, { level_override: e.target.value })} className="bg-transparent text-xs border border-border rounded px-1 py-0.5" data-testid={`jd-level-${d.id}`}>
                    <option value="">?</option>{LEVELS.map((l) => <option key={l}>{l}</option>)}
                  </select>
                </td>
                <td className="px-3 text-xs text-slate-400">{d.location}</td>
                <td className="px-3 text-xs font-mono text-slate-400">{d.date_posted}</td>
                <td className="px-3 text-xs text-slate-400">{d.source}</td>
                <td className="px-3"><span title={d.status_reason} className={`text-[11px] px-2 py-0.5 rounded-full border ${ST[d.status]}`}>{d.status.replace("_", " ")}</span></td>
                <td className="px-3 text-right whitespace-nowrap">
                  <button title="View" onClick={() => setView(d)} className="p-1 text-slate-400 hover:text-slate-100" data-testid={`jd-view-${d.id}`}><Eye size={14} /></button>
                  <button title="Force include" onClick={() => patch(d, { status: "usable" })} className="p-1 text-slate-400 hover:text-emerald-300" data-testid={`jd-pin-${d.id}`}><Pin size={14} /></button>
                  <button title="Exclude" onClick={() => patch(d, { status: "excluded" })} className="p-1 text-slate-400 hover:text-rose-300" data-testid={`jd-exclude-${d.id}`}><Ban size={14} /></button>
                </td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={8} className="text-center py-12 text-muted-foreground">No JDs yet. Run "Collect JDs" or paste JDs manually.</td></tr>}
          </tbody>
        </table>
      </div>
      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-auto scroll-thin bg-card">
          <DialogHeader><DialogTitle>{view?.title} · {view?.company}</DialogTitle></DialogHeader>
          <div className="text-xs text-amber-300">{view?.status_reason}</div>
          <pre className="whitespace-pre-wrap text-xs text-slate-300 font-mono">{view?.full_text}</pre>
        </DialogContent>
      </Dialog>
      <ManualJD open={manual} onClose={() => setManual(false)} jobId={job.id} onAdded={load} />
    </StageShell>
  );
}
